/** Shared defaults for AI Book Assistant + seven-step Book Setup. */

export const EXAMPLE_PROMPTS = [
  "Create a beginner-friendly business book about getting funding.",
  "Build a workbook for people starting their first online business.",
  "Write a children's book about courage and friendship.",
  "Create a Haitian Creole guide for starting a food business in America.",
  "Turn my personal story into an inspiring memoir.",
  "Create a practical lead magnet with examples and checklists.",
  "Write a how-to guide that teaches readers one skill step by step.",
];

export const STARTER_PROMPTS = {
  business:
    "Create a professional business book for first-time entrepreneurs. Include practical frameworks, examples, checklists, chapter summaries, and a clear action plan.",
  workbook:
    "Create an interactive workbook with short lessons, fill-in-the-blank exercises, reflection questions, checkboxes, answer spaces, action plans, and a final review section.",
  howto:
    "Create a beginner-friendly how-to guide that explains the topic step by step. Include examples, common mistakes, checklists, resources, and an implementation plan.",
  children:
    "Create a children's book for ages 5 to 9 with memorable characters, a simple conflict, a positive lesson, short pages, illustration suggestions, and a warm ending.",
  memoir:
    "Create a personal memoir with a strong opening story, chronological chapters, defining moments, lessons learned, reflection, and an emotionally satisfying conclusion.",
  leadmagnet:
    "Create a concise lead magnet that solves one urgent problem for a specific audience. Include a quick-start framework, examples, a checklist, and a strong call to action.",
};

export const GUIDED_QUESTIONS = [
  { key: "topic", label: "What is the book about?", type: "textarea", placeholder: "Describe the main topic or idea." },
  { key: "reader", label: "Who is the intended reader?", type: "textarea", placeholder: "Describe the ideal reader." },
  { key: "outcome", label: "What should readers know or accomplish?", type: "textarea", placeholder: "Describe the result or transformation." },
  {
    key: "bookType",
    label: "What type of book should it be?",
    type: "select",
    options: ["Business book", "How-to guide", "Workbook", "Lead magnet", "Memoir", "Children's book", "Course companion", "Journal"],
  },
  {
    key: "length",
    label: "How long should it be?",
    type: "select",
    options: [
      "Short guide — about 10,000 words",
      "Standard book — about 30,000 words",
      "Long-form book — about 50,000 words",
      "I am not sure",
    ],
  },
  {
    key: "elements",
    label: "What should it include?",
    type: "textarea",
    placeholder: "Examples, exercises, checklists, images, diagrams, disclaimers, tables, worksheets...",
  },
  {
    key: "publish",
    label: "Where will it be published?",
    type: "select",
    options: [
      "Amazon KDP paperback",
      "Amazon KDP hardcover",
      "PDF download",
      "EPUB ebook",
      "Lead magnet PDF",
      "I am not sure",
    ],
  },
  {
    key: "tone",
    label: "What writing tone should it use?",
    type: "select",
    options: [
      "Professional and practical",
      "Friendly and conversational",
      "Inspirational",
      "Academic",
      "Direct and persuasive",
    ],
  },
];

export const SETUP_STEPS = [
  { id: "start", label: "Start" },
  { id: "info", label: "Book info" },
  { id: "structure", label: "Structure" },
  { id: "format", label: "Format" },
  { id: "design", label: "Design" },
  { id: "publish", label: "Publish" },
  { id: "review", label: "Review" },
];

export const TRIM_PRESETS = [
  { id: "5x8", label: "5 × 8 inches", desc: "Compact memoir, poetry, and short nonfiction." },
  { id: "5.5x8.5", label: "5.5 × 8.5 inches", desc: "Popular for fiction and personal development." },
  { id: "6x9", label: "6 × 9 inches", desc: "Recommended for business, self-help, and nonfiction." },
  { id: "7x10", label: "7 × 10 inches", desc: "Ideal for educational books and workbooks." },
  { id: "8.5x11", label: "8.5 × 11 inches", desc: "Large workbook, planner, or training manual." },
  { id: "8x8", label: "8 × 8 inches", desc: "Square format for children's and visual books." },
  { id: "a4", label: "A4", desc: "International reports, manuals, and print guides." },
  { id: "custom", label: "Custom size", desc: "Enter exact publishing specifications." },
];

export const DESIGN_THEMES = [
  { id: "modern", label: "Modern", desc: "Clean typography and balanced spacing." },
  { id: "classic", label: "Classic", desc: "Traditional serif book styling." },
  { id: "minimal", label: "Minimal", desc: "Quiet design with generous white space." },
  { id: "editorial", label: "Editorial", desc: "Bold headings and magazine-inspired pages." },
  { id: "business", label: "Business", desc: "Professional layouts for experts." },
  { id: "luxury", label: "Luxury", desc: "Elegant typography and refined openings." },
  { id: "children", label: "Children's", desc: "Large type and image-friendly pages." },
  { id: "workbook", label: "Workbook", desc: "Exercises, prompts, tables, and writing space." },
];

export const PUBLISH_DESTINATIONS = [
  { id: "kdp_paperback", label: "Amazon KDP paperback", desc: "KDP-compatible print-ready interior." },
  { id: "kdp_hardcover", label: "Amazon KDP hardcover", desc: "Hardcover-ready interior and binding settings." },
  { id: "epub", label: "EPUB", desc: "Reflowable ebook for digital readers." },
  { id: "pdf_standard", label: "Standard PDF", desc: "Digital reading, sharing, and delivery." },
  { id: "pdf_print", label: "Print-ready PDF", desc: "High-resolution file with bleed settings." },
  { id: "pdf_lead", label: "Lead magnet PDF", desc: "Optimized for funnels and downloads." },
  { id: "private", label: "Private digital book", desc: "Member-only or internal distribution." },
  { id: "unsure", label: "Not sure yet", desc: "Use flexible defaults and decide later." },
];

export const FRONT_BACK_MATTER = [
  { key: "copyright", label: "Copyright page", default: true },
  { key: "dedication", label: "Dedication", default: false },
  { key: "acknowledgments", label: "Acknowledgments", default: false },
  { key: "toc", label: "Table of contents", default: true },
  { key: "introduction", label: "Introduction", default: true },
  { key: "conclusion", label: "Conclusion", default: true },
  { key: "resources", label: "Resources", default: true },
  { key: "author_bio", label: "About the author", default: true },
  { key: "cta", label: "Call to action", default: true },
  { key: "legal", label: "Legal disclaimer", default: true },
];

export function newChapterId() {
  return `sec-${Math.random().toString(36).slice(2, 10)}`;
}

export function emptySetupState() {
  const matter = {};
  FRONT_BACK_MATTER.forEach((m) => { matter[m.key] = m.default; });
  return {
    currentStep: 0,
    prompt: "",
    guidedAnswers: Object.fromEntries(GUIDED_QUESTIONS.map((q) => [q.key, ""])),
    blueprint: null,
    fieldOrigins: {},
    creationPath: "Create with AI",
    title: "",
    subtitle: "",
    author: "PremierLab Author",
    language: "English",
    bookType: "Business book",
    genre: "",
    audience: "",
    readerOutcome: "",
    tone: "Clear and professional",
    approximateLength: "Standard book — 30,000 words",
    writingPreferences: {
      includeActions: true,
      includeExamples: true,
      includeExercises: false,
      includeSummaries: true,
      includeChecklists: true,
      requireCitations: false,
    },
    structureMode: "Generate with AI",
    chapterCount: 10,
    averageChapterLength: "Standard — 2,000 to 3,000 words",
    chapters: [],
    assumptions: [],
    frontBackMatter: matter,
    trimSize: "6 × 9 inches",
    documentSettings: {
      width: 6,
      height: 9,
      units: "Inches",
      estimatedPages: 120,
      startPage: 1,
      orientation: "Portrait",
      facingPages: "Yes",
      primaryTextFrame: "No",
      columns: 1,
      columnGutter: 0.1667,
      marginTop: 0.75,
      marginBottom: 0.75,
      marginInside: 0.875,
      marginOutside: 0.625,
      bleedTop: 0.125,
      bleedBottom: 0.125,
      bleedInside: 0.125,
      bleedOutside: 0.125,
      slugTop: 0,
      slugBottom: 0,
      slugInside: 0,
      slugOutside: 0,
    },
    designTheme: "Modern",
    bodyFont: "Inter",
    headingFont: "Inter",
    bodyFontSize: "11 pt",
    lineSpacing: 1.5,
    chapterOpening: "Full title page",
    pageNumberPosition: "Bottom center",
    publishingDestination: "Amazon KDP paperback",
    printSettings: {
      binding: "Paperback",
      readingDirection: "Left to right",
      interiorPrinting: "Black and white",
      paperType: "White paper",
    },
    reviewPrefs: {
      showCreditCost: true,
      requireConfirm: true,
      keepEditable: true,
    },
    estimatedCredits: 18,
  };
}

export function combineGuidedAnswers(answers) {
  const topic = (answers.topic || "").trim() || "a practical topic";
  const reader = (answers.reader || "").trim() || "beginners";
  const outcome = (answers.outcome || "").trim() || "understand and apply the main ideas";
  const type = answers.bookType || "How-to guide";
  const length = answers.length || "Standard book — about 30,000 words";
  const elements = (answers.elements || "").trim() || "examples, checklists, and action steps";
  const publish = answers.publish || "Amazon KDP paperback";
  const tone = answers.tone || "Professional and practical";
  return (
    `Create a ${String(type).toLowerCase()} about ${topic}. ` +
    `The book is for ${reader}. ` +
    `Readers should be able to ${outcome}. ` +
    `Use a ${String(tone).toLowerCase()} tone. ` +
    `Target length: ${length}. ` +
    `Include ${elements}. ` +
    `Prepare it for ${publish}.`
  );
}

export function markFieldOrigin(origins, key, origin) {
  return { ...origins, [key]: origin };
}

const STRUCTURE_TYPES = new Set([
  "front_matter", "part", "chapter", "introduction", "conclusion",
  "appendix", "back_matter", "author_bio", "back_cover",
]);

function asString(v, fallback = "") {
  if (v == null) return fallback;
  if (typeof v === "string") return v.trim();
  if (typeof v === "number") return String(v);
  return fallback;
}

function inferStructureType(item, index, total) {
  const raw = asString(item?.type || item?.section_type || item?.kind).toLowerCase();
  if (STRUCTURE_TYPES.has(raw)) return raw;
  const title = asString(item?.title || item).toLowerCase();
  if (title.includes("introduction") || title.startsWith("intro")) return "introduction";
  if (title.includes("conclusion") || title.includes("ninety days")) return "conclusion";
  if (title.includes("appendix")) return "appendix";
  if (title.includes("author") || title.includes("about the author")) return "author_bio";
  if (title.includes("back cover")) return "back_cover";
  if (index === 0 && total > 1 && title.includes("start")) return "introduction";
  return "chapter";
}

function normalizeStructureItem(item, index, total) {
  if (item == null) return null;
  if (typeof item === "string") {
    return {
      id: newChapterId(),
      type: "chapter",
      title: item,
      description: "",
      goal: "",
      order: index + 1,
      part_title: null,
      children: [],
    };
  }
  const title = asString(item.title || item.name || `Chapter ${index + 1}`);
  const description = asString(item.description || item.summary || "");
  const goal = asString(item.goal || item.hook || description);
  return {
    id: asString(item.id) || newChapterId(),
    type: inferStructureType(item, index, total),
    title,
    description,
    goal,
    order: Number(item.order) > 0 ? Number(item.order) : index + 1,
    part_title: asString(item.part_title || item.partTitle || item.part) || null,
    children: Array.isArray(item.children)
      ? item.children.map((c, i) => normalizeStructureItem(c, i, item.children.length)).filter(Boolean)
      : [],
  };
}

function mapLengthLabel(words) {
  const n = Number(String(words).replace(/[^\d]/g, "")) || 0;
  if (n > 0 && n <= 15000) return "Short guide — 10,000 words";
  if (n >= 40000) return "Long-form book — 50,000 words";
  if (n > 0) return "Standard book — 30,000 words";
  return asString(words) || "Standard book — 30,000 words";
}

/**
 * Normalize AI/backend blueprint payloads into one canonical object.
 * Supports aliases: type/bookType, reader/audience, outcome/readerOutcome,
 * words/targetWords, pages/estimatedPages, trim/trimSize, outline/structure.
 */
export function normalizeBlueprint(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      title: "",
      subtitle: "",
      author: "PremierLab Author",
      bookType: "How-to guide",
      genre: "",
      audience: "",
      readerOutcome: "",
      tone: "Clear and professional",
      language: "English",
      targetWords: 30000,
      estimatedPages: 120,
      trimSize: "6 × 9 inches",
      destination: "Amazon KDP paperback",
      theme: "Modern",
      writingPreferences: null,
      structure: [],
      assumptions: [],
      estimatedCredits: 2,
      approximateLength: "Standard book — 30,000 words",
    };
  }
  const outline = raw.structure || raw.outline || raw.chapters || [];
  const list = Array.isArray(outline) ? outline : [];
  const structure = list
    .map((item, i) => normalizeStructureItem(item, i, list.length))
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .map((item, i) => ({ ...item, order: i + 1 }));

  const wordRaw = raw.targetWords ?? raw.target_words ?? raw.wordCount ?? raw.word_count ?? raw.words;
  const pagesRaw = raw.estimatedPages ?? raw.estimated_pages ?? raw.pages;
  const prefs = raw.writingPreferences || raw.writing_preferences || null;

  return {
    title: asString(raw.title),
    subtitle: asString(raw.subtitle),
    author: asString(raw.author) || "PremierLab Author",
    bookType: asString(raw.bookType || raw.book_type || raw.type) || "How-to guide",
    genre: asString(raw.genre || raw.category),
    audience: asString(raw.audience || raw.targetReader || raw.reader),
    readerOutcome: asString(raw.readerOutcome || raw.reader_outcome || raw.outcome),
    tone: asString(raw.tone) || "Clear and professional",
    language: asString(raw.language) || "English",
    targetWords: Number(String(wordRaw ?? "").replace(/[^\d]/g, "")) || 30000,
    estimatedPages: Number(pagesRaw) || 120,
    trimSize: asString(raw.trimSize || raw.trim_size || raw.trim) || "6 × 9 inches",
    destination: asString(raw.destination || raw.publishingDestination) || "Amazon KDP paperback",
    theme: asString(raw.theme || raw.designTheme) || "Modern",
    writingPreferences: prefs && typeof prefs === "object" ? {
      includeActions: !!(prefs.actionSteps ?? prefs.includeActions ?? true),
      includeExamples: !!(prefs.examples ?? prefs.includeExamples ?? true),
      includeExercises: !!(prefs.exercises ?? prefs.includeExercises ?? false),
      includeSummaries: !!(prefs.chapterSummaries ?? prefs.includeSummaries ?? true),
      includeChecklists: !!(prefs.checklists ?? prefs.includeChecklists ?? true),
      requireCitations: !!(prefs.citations ?? prefs.requireCitations ?? false),
    } : null,
    structure,
    assumptions: Array.isArray(raw.assumptions) ? raw.assumptions.map((a) => asString(a)).filter(Boolean) : [],
    estimatedCredits: Number(raw.estimatedCredits ?? raw.estimated_credits) || 2,
    approximateLength: mapLengthLabel(wordRaw),
    // Keep raw aliases for display cards that still read targetReader etc.
    targetReader: asString(raw.audience || raw.targetReader || raw.reader),
    outcome: asString(raw.readerOutcome || raw.reader_outcome || raw.outcome),
    wordCount: asString(wordRaw) || "30000",
    pages: Number(pagesRaw) || 120,
    trim: asString(raw.trimSize || raw.trim_size || raw.trim) || "6 × 9 inches",
    outline: structure.map((s) => ({ title: s.title, description: s.description || s.goal })),
  };
}

/** Merge a normalized blueprint into central setup state (single source of truth). */
export function applyBlueprintToSetup(setup, rawBlueprint) {
  const bp = normalizeBlueprint(rawBlueprint);
  const chapters = bp.structure.map((s) => ({
    id: s.id,
    type: s.type,
    title: s.title,
    description: s.description || s.goal || "",
    goal: s.goal || s.description || "",
    part_title: s.part_title,
    order: s.order,
  }));
  const originKeys = [
    "title", "subtitle", "author", "bookType", "genre", "audience", "readerOutcome",
    "tone", "language", "approximateLength", "trimSize", "publishingDestination",
    "designTheme", "chapterCount",
  ];
  return {
    ...setup,
    blueprint: bp,
    title: bp.title || setup.title,
    subtitle: bp.subtitle,
    author: bp.author || setup.author,
    bookType: bp.bookType || setup.bookType,
    genre: bp.genre,
    audience: bp.audience,
    readerOutcome: bp.readerOutcome,
    tone: bp.tone || setup.tone,
    language: bp.language || setup.language,
    approximateLength: bp.approximateLength || setup.approximateLength,
    trimSize: bp.trimSize || setup.trimSize,
    publishingDestination: bp.destination || setup.publishingDestination,
    designTheme: bp.theme || setup.designTheme,
    writingPreferences: bp.writingPreferences || setup.writingPreferences,
    assumptions: bp.assumptions,
    chapters,
    chapterCount: chapters.length || setup.chapterCount,
    estimatedCredits: bp.estimatedCredits ?? setup.estimatedCredits,
    documentSettings: {
      ...setup.documentSettings,
      estimatedPages: bp.estimatedPages || setup.documentSettings.estimatedPages,
    },
    fieldOrigins: {
      ...setup.fieldOrigins,
      ...Object.fromEntries(originKeys.map((k) => [k, "ai"])),
    },
    currentStep: 1,
    creationPath: setup.creationPath || "Create with AI",
  };
}

export function validateBookInfo(setup) {
  const missing = [];
  if (!asString(setup.title)) missing.push("title");
  if (!asString(setup.subtitle)) missing.push("subtitle");
  if (!asString(setup.author)) missing.push("author");
  if (!asString(setup.language)) missing.push("language");
  if (!asString(setup.bookType)) missing.push("book type");
  if (!asString(setup.genre)) missing.push("genre");
  if (!asString(setup.audience)) missing.push("audience");
  if (!asString(setup.readerOutcome)) missing.push("reader outcome");
  if (!asString(setup.tone)) missing.push("tone");
  if (!asString(setup.approximateLength)) missing.push("length");
  return missing;
}
